const NAV_IMAGE_KEYS = ['preview', 'preview_mobile', 'preview_alt']
const UNUSED_BANNER_KEYS = ['other_page_desktop', 'other_page_tablet', 'other_page_mobile']
const BLOCKS_WITHOUT_IMAGE = new Set(['title-content', 'title-content-video', 'title-content-link'])

function deleteKeys(object, keys) {
  if (!object || typeof object !== 'object' || Array.isArray(object)) {
    return
  }

  keys.forEach((key) => {
    if (key in object) {
      delete object[key]
    }
  })
}

function stripNavImages(direction) {
  if (!direction) {
    return
  }

  deleteKeys(direction, NAV_IMAGE_KEYS)

  if (Array.isArray(direction.children)) {
    direction.children.forEach(stripNavImages)
  }

  if (Array.isArray(direction.subdirections)) {
    direction.subdirections.forEach(stripNavImages)
  }
}

export function slimDirectionRef(direction) {
  if (!direction || typeof direction !== 'object') {
    return direction
  }

  return {
    id: direction.id,
    title: direction.title,
    h1: direction.h1,
    slug: direction.slug,
    service_count: direction.service_count,
  }
}

export function slimServiceDirections(services = []) {
  return services.map((service) => {
    if (!service?.direction) {
      return service
    }

    return {
      ...service,
      direction: slimDirectionRef(service.direction),
    }
  })
}

export function slimHubService(service) {
  if (!service || typeof service !== 'object') return null

  const next = {
    id: service.id,
    title: service.title,
    slug: service.slug,
  }

  if (service.preview) next.preview = service.preview
  if (service.preview_mobile) next.preview_mobile = service.preview_mobile
  if (service.preview_alt) next.preview_alt = service.preview_alt
  if (service.theme) next.theme = service.theme
  if (service.page_type) next.page_type = service.page_type

  const parentSlug = service.direction && service.direction.slug
  if (parentSlug) next.direction = { slug: parentSlug }

  return next
}

export function slimHubServices(services = []) {
  if (!Array.isArray(services)) return []
  return services.map(slimHubService).filter(Boolean)
}

export function slimSpecialistIds(list = []) {
  if (!Array.isArray(list)) return []

  const seen = new Set()
  const ids = []

  list.forEach((item) => {
    const id = item && typeof item === 'object' ? item.id : item
    if (id == null || id === '' || seen.has(String(id))) return
    seen.add(String(id))
    ids.push({ id })
  })

  return ids
}

function slimPerson(person) {
  if (!person || typeof person !== 'object') return person

  const next = {}
  ;['id', 'slug', 'first_name', 'last_name', 'patronymic'].forEach((key) => {
    if (person[key] != null && person[key] !== '') next[key] = person[key]
  })
  return next
}

export function slimPeople(value) {
  if (Array.isArray(value)) return value.map(slimPerson).filter(Boolean)
  return slimPerson(value)
}

const HUB_DIRECTION_KEYS = ['id', 'title', 'h1', 'slug', 'service_count']
const HUB_DIRECTION_PREVIEW_KEYS = ['preview', 'preview_mobile', 'preview_alt', 'page_type', 'theme']

export function slimHubDirection(direction, { keepPreview = false } = {}) {
  if (!direction || typeof direction !== 'object') return null

  const next = {}
  HUB_DIRECTION_KEYS.forEach((key) => {
    if (direction[key] != null) next[key] = direction[key]
  })

  if (keepPreview) {
    HUB_DIRECTION_PREVIEW_KEYS.forEach((key) => {
      if (direction[key] != null) next[key] = direction[key]
    })
  }

  if (Array.isArray(direction.children) && direction.children.length) {
    next.children = direction.children.map((child) => slimHubDirection(child, { keepPreview })).filter(Boolean)
  }

  return next
}

export function slimHubDirections(list = [], options) {
  if (!Array.isArray(list)) return []
  return list.map((item) => slimHubDirection(item, options)).filter(Boolean)
}

function slimSeoService(service, parentSlug) {
  const slug = service && service.slug
  const directionSlug = (service && service.direction && service.direction.slug) || parentSlug
  if (!slug || !directionSlug) return null

  return {
    id: service.id,
    slug,
    title: service.title,
    direction: { slug: directionSlug },
  }
}

export function slimSeoGroups(groups = []) {
  if (!Array.isArray(groups)) return []
  if (!groups.some((group) => group && group.slug)) return groups

  return groups
    .map((group) => {
      if (!group || !group.slug) return null

      return {
        slug: group.slug,
        title: group.title,
        services: (group.services || []).map((service) => slimSeoService(service, group.slug)).filter(Boolean),
        children: slimSeoGroups(group.children || []),
      }
    })
    .filter(Boolean)
}

const DIRECTION_PAGE_KEYS = [
  'id',
  'title',
  'h1',
  'slug',
  'banner',
  'banner_alt',
  'blocks',
  'questions',
  'gallery',
  'short_content',
  'meta',
  'form_direction',
  'form_doctor',
  'description',
  'dateModified',
  'date_modified',
  'updated_at',
  'modified_at',
  'last_reviewed',
]

function pickFields(source, keys) {
  const next = {}
  if (!source || typeof source !== 'object') return next
  keys.forEach((key) => {
    if (source[key] != null) next[key] = source[key]
  })
  return next
}

function slimFormDirection(value) {
  if (!value || typeof value !== 'object') return value
  return value.slug || value
}

function slimFormDoctor(value) {
  if (!value || typeof value !== 'object') return value
  if (value.id != null && value.id !== '') return value.id
  return value
}

export function slimDirectionPayload({
  data,
  directions,
  services,
  specialists,
  current_direction,
  short_content,
} = {}) {
  const next = pickFields(data, DIRECTION_PAGE_KEYS)
  deleteKeys(next.banner, UNUSED_BANNER_KEYS)
  ;(next.blocks || []).forEach((block) => {
    if (BLOCKS_WITHOUT_IMAGE.has(block?.type)) delete block.data?.image
  })
  next.directions = slimHubDirections(directions)
  next.services = slimHubServices(services)
  next.specialists = slimSpecialistIds(specialists)
  next.current_direction = slimDirectionRef(current_direction) || current_direction
  if (short_content != null) next.short_content = short_content
  if (data && data.direction) {
    const parent = slimDirectionRef(data.direction) || {}
    if (data.direction.procedure_type) parent.procedure_type = data.direction.procedure_type
    next.direction = parent
  }
  next.form_direction = slimFormDirection(next.form_direction)
  next.form_doctor = slimFormDoctor(next.form_doctor)
  return next
}

const SERVICE_PAGE_KEYS = [
  'id',
  'title',
  'h1',
  'slug',
  'description',
  'short_content',
  'blocks',
  'questions',
  'banner',
  'banner_alt',
  'meta',
  'price',
  'preparation',
  'followup',
  'procedure_type',
  'last_reviewed',
  'dateModified',
  'date_modified',
  'updated_at',
  'modified_at',
  'form_direction',
  'form_doctor',
]

export function slimServicePayload(data = {}) {
  const next = pickFields(data, SERVICE_PAGE_KEYS)
  deleteKeys(next.banner, UNUSED_BANNER_KEYS)
  ;(next.blocks || []).forEach((block) => {
    if (BLOCKS_WITHOUT_IMAGE.has(block?.type)) delete block.data?.image
  })
  next.directions = slimHubDirections(data.directions)
  next.recommendations = slimHubServices(data.recommendations)
  next.specialists = slimPeople(data.specialists || [])
  ;['author', 'specialist', 'physician', 'reviewed_by'].forEach((key) => {
    if (data[key]) next[key] = slimPeople(data[key])
  })
  if (data.direction && typeof data.direction === 'object') {
    const parent = slimDirectionRef(data.direction) || {}
    if (data.direction.procedure_type) parent.procedure_type = data.direction.procedure_type
    next.direction = parent
  }
  next.form_direction = slimFormDirection(next.form_direction)
  next.form_doctor = slimFormDoctor(next.form_doctor)
  return next
}

function stripNavImageList(list) {
  if (!Array.isArray(list)) {
    return
  }

  list.forEach(stripNavImages)
}

export default function stripUnrenderedDirectionImages({
  data,
  directions,
  current_direction,
  services,
  directionsData,
} = {}) {
  stripNavImageList(data?.directions)
  stripNavImageList(directions)
  stripNavImages(data?.direction)
  stripNavImages(current_direction)
  stripNavImages(directionsData?.current_direction)
  deleteKeys(data?.banner, UNUSED_BANNER_KEYS)

  ;(data?.blocks || []).forEach((block) => {
    if (BLOCKS_WITHOUT_IMAGE.has(block?.type)) {
      delete block.data?.image
    }
  })

  ;(services || []).forEach((service) => stripNavImages(service?.direction))

  if (directionsData) {
    delete directionsData.banners
    delete directionsData.gallery
    ;(directionsData.specialists || []).forEach((specialist) => {
      deleteKeys(specialist, ['image', 'preview', 'preview_mobile', 'preview_alt'])
    })
  }
}
